import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Activity, RefreshCw, Power, Sparkles, Info } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const ICON_FOR = {
  apple_health: "🍎",
  google_health_connect: "🅖",
  fitbit: "🟦",
};

function relTime(iso) {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function SettingsIntegrations() {
  const nav = useNavigate();
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState({});

  const load = async () => setProviders((await api.get("/health/providers")).data);
  useEffect(() => { load(); }, []);

  const setBusyFor = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  const connect = async (p) => {
    setBusyFor(p.provider, "connect");
    try {
      await api.post("/health/connect", { provider: p.provider });
      toast.success(`${p.label} connected`);
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusyFor(p.provider, null); }
  };
  const disconnect = async (p) => {
    setBusyFor(p.provider, "disconnect");
    try {
      await api.post("/health/disconnect", { provider: p.provider });
      toast(`${p.label} disconnected`);
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusyFor(p.provider, null); }
  };
  const sync = async (p) => {
    setBusyFor(p.provider, "sync");
    try {
      const r = await api.post("/health/sync", { provider: p.provider, days: 1 });
      toast.success(`Synced · +${r.data.total_steps.toLocaleString()} steps · +${r.data.points_earned} pts`);
      (r.data.new_badges || []).forEach((b) =>
        toast(`🏅 Badge: ${b.title}`, { description: `${b.milestone_pct}% of annual goal` })
      );
      load();
    } catch (e) { toast.error("Sync failed"); }
    finally { setBusyFor(p.provider, null); }
  };

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="integrations-back">
          <ChevronLeft size={16} /> Back
        </button>
        <h1 className="ps-heading text-3xl font-bold mt-4">Health Integrations</h1>
        <p className="text-slate-400 text-sm">Connect a provider, then tap Sync to pull steps into Posture Steps.</p>

        <div className="mt-4 ps-card p-4 flex gap-3">
          <Info size={18} color="#00B4D8" className="shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400 leading-relaxed">
            On the web, we use a simulated sync that mirrors how the native iOS & Android apps pull data from
            Apple Health, Google Health Connect and Fitbit. Disconnect anytime — your historical data stays.
          </p>
        </div>

        <div className="mt-5 space-y-3">
          {providers.map((p) => (
            <div key={p.provider} className={`ps-card p-5 ${p.connected ? "border-[#2fbf71]/30" : ""}`} data-testid={`provider-${p.provider}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#142534] flex items-center justify-center text-2xl">
                  {ICON_FOR[p.provider] || "❤️"}
                </div>
                <div className="flex-1">
                  <div className="ps-heading font-semibold">{p.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {p.connected ? (
                      <span className="inline-flex items-center gap-1">
                        <Sparkles size={10} color="#2fbf71" /> Connected · last sync {relTime(p.last_sync)}
                      </span>
                    ) : "Not connected"}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                {p.connected ? (
                  <>
                    <button
                      data-testid={`sync-${p.provider}`}
                      onClick={() => sync(p)}
                      disabled={busy[p.provider] === "sync"}
                      className="flex-1 ps-button-primary text-xs py-2.5 inline-flex items-center justify-center gap-1"
                    >
                      <RefreshCw size={14} className={busy[p.provider] === "sync" ? "animate-spin" : ""} />
                      {busy[p.provider] === "sync" ? "Syncing…" : "Sync now"}
                    </button>
                    <button
                      data-testid={`disconnect-${p.provider}`}
                      onClick={() => disconnect(p)}
                      disabled={busy[p.provider] === "disconnect"}
                      className="px-4 rounded-full border border-[#1a2f40] text-xs text-slate-300 inline-flex items-center gap-1"
                    >
                      <Power size={14} /> Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    data-testid={`connect-${p.provider}`}
                    onClick={() => connect(p)}
                    disabled={busy[p.provider] === "connect"}
                    className="w-full ps-button-primary text-xs py-2.5 inline-flex items-center justify-center gap-1"
                  >
                    <Activity size={14} /> {busy[p.provider] === "connect" ? "Connecting…" : "Connect"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PhoneFrame>
  );
}
